import React from 'react';
import {
  pickTransfer,
  receiveTransfer,
  reportTransferProblem,
  transferActionUnanswered,
  type TransferRecord,
} from '@/data/transferRecord';
import { resolveWarehouseScanSources } from '@/data/inventoryScanSources';
import {
  pluralize,
  type ScanCountProblem,
  type ScanCountResolution,
  type ScanCountSubmission,
  type ScanCountSubmitAttempt,
  type ScanCountSubmitOutcome,
} from '@/domain/warehouse';
import ScanCountDialog from '../../../components/receiving/ScanCountDialog';
import {
  buildPickLines,
  buildReceiveLines,
  lineDisplayName,
  pickPlanFromSubmission,
  problemKindFor,
  receiveLinesFromSubmission,
  siteName,
  type TransferActionViews,
} from './transferRecordView';
import type { TransferActionRunner } from './useTransferRecord';

export type TransferScanMode = { verb: 'Pick' } | { verb: 'Receive'; lineIds: string[] | null };

type TransferScanDialogProps = {
  record: TransferRecord;
  actions: TransferActionViews;
  mode: TransferScanMode;
  runner: TransferActionRunner;
  /** The signed-in user, so a count in progress is kept per person. */
  draftOwner: string | null;
  onClose: () => void;
  /** A success message to show once the count is applied (the dialog closes itself). */
  onApplied: (message: string) => void;
};

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * The transfer's adapter for the shared Receive/Pick dialog: the record's lines as dialog lines,
 * submissions as transfer_pick / transfer_receive calls, unknown codes looked up at the shipping
 * site (Pick), and "Report a problem" noted on the record.
 */
export default function TransferScanDialog({
  record,
  actions,
  mode,
  runner,
  draftOwner,
  onClose,
  onApplied,
}: TransferScanDialogProps) {
  const { transfer } = record;
  const verb = mode.verb;
  const receiveIds = mode.verb === 'Receive' ? mode.lineIds : null;
  const receiveKey = receiveIds ? receiveIds.join(',') : '';
  const lines = React.useMemo(
    () => (verb === 'Pick' ? buildPickLines(record.lines) : buildReceiveLines(record.lines, receiveKey ? receiveKey.split(',') : null)),
    [receiveKey, record.lines, verb],
  );
  const fromSiteId = transfer.fromSite?.id ?? null;
  const from = siteName(transfer.fromSite, 'the shipping site');
  const to = siteName(transfer.toSite, 'the receiving site');

  const resolvePickScan = React.useCallback(async (code: string): Promise<ScanCountResolution | null> => {
    if (!fromSiteId) return null;
    const sources = await resolveWarehouseScanSources(code, fromSiteId);
    const open = record.lines.filter((line) => line.status !== 'cancelled' && line.qty > 0);
    const forItem = (itemId: string, tracking: 'serial' | 'quantity') => {
      const matches = open.filter((line) => line.tracking === tracking && line.item?.id === itemId);
      return matches.find((line) => line.pickedQty < line.qty) ?? matches[0] ?? null;
    };
    for (const source of sources) {
      if (source.sourceKind === 'owned_asset' && source.assetId) {
        const line = forItem(source.itemId, 'serial');
        if (line) return { lineId: line.id, serialId: source.assetId, code: source.barcode ?? source.serialNumber ?? code };
      }
      if (source.sourceKind === 'owned_bulk') {
        const line = forItem(source.itemId, 'quantity');
        if (line) return { lineId: line.id };
      }
    }
    return null;
  }, [fromSiteId, record.lines]);

  // A retry ("Try again" after no answer came back) resends the plan exactly as it was first sent,
  // under the same request key: pick quantities are absolute, and the record has moved since.
  const submitPick = React.useCallback(async (submission: ScanCountSubmission, attempt?: ScanCountSubmitAttempt): Promise<ScanCountSubmitOutcome> => {
    const plan = pickPlanFromSubmission(submission, lines);
    if (!plan.ops.length) return undefined;
    let sent = plan;
    try {
      const result = await runner.run(
        'pick',
        plan,
        (requestKey, payload) => {
          sent = payload;
          return pickTransfer(transfer.id, requestKey, payload.ops);
        },
        { movesStock: true, fallback: 'Nothing was picked.', attempt: attempt?.retry ? 'retry' : 'new' },
      );
      const failedLines = new Set<string>();
      let firstError: string | null = null;
      for (const outcome of result.results ?? []) {
        if (outcome.ok) continue;
        const lineId = sent.opLineIds[outcome.opIndex] ?? outcome.lineId;
        if (lineId) failedLines.add(lineId);
        firstError = firstError ?? outcome.message;
      }
      if (!failedLines.size) {
        const units = submission.reduce((sum, entry) => sum + (entry.serialIds?.length ?? entry.qty ?? 0), 0);
        onApplied(`Picked ${pluralize(units, 'unit')}`);
        return undefined;
      }
      const appliedLineIds = submission.map((entry) => entry.lineId).filter((lineId) => !failedLines.has(lineId));
      const names = record.lines.filter((line) => failedLines.has(line.id)).map(lineDisplayName);
      return {
        appliedLineIds,
        error: `${firstError ?? 'Some units were not picked.'}${names.length ? ` (${names.slice(0, 3).join(', ')}${names.length > 3 ? '…' : ''})` : ''}`,
      };
    } catch (error) {
      return { error: errorText(error, 'Nothing was picked.'), outcomeUnknown: transferActionUnanswered(error) };
    }
  }, [lines, onApplied, record.lines, runner, transfer.id]);

  // Receipts are counts to add, so a retry must resend the very same lines under the same key.
  const submitReceive = React.useCallback(async (submission: ScanCountSubmission, attempt?: ScanCountSubmitAttempt): Promise<ScanCountSubmitOutcome> => {
    const payload = receiveLinesFromSubmission(submission);
    if (!payload.length) return undefined;
    try {
      const result = await runner.run(
        'receive',
        payload,
        (requestKey, sent) => receiveTransfer(transfer.id, requestKey, sent, null),
        { movesStock: true, fallback: 'Nothing was received.', attempt: attempt?.retry ? 'retry' : 'new' },
      );
      const counted = submission.reduce((sum, entry) => sum + (entry.serialIds?.length ?? entry.qty ?? 0), 0);
      const receivedUnits = Number(result.raw.received_units);
      onApplied(`Received ${pluralize(Number.isFinite(receivedUnits) && receivedUnits > 0 ? receivedUnits : counted, 'unit')} at ${to}`);
      return undefined;
    } catch (error) {
      return { error: errorText(error, 'Nothing was received.'), outcomeUnknown: transferActionUnanswered(error) };
    }
  }, [onApplied, runner, to, transfer.id]);

  const reportProblem = React.useCallback(async (problem: ScanCountProblem) => {
    const payload = { kind: problemKindFor(problem.kind), note: problem.note, lineId: problem.lineId ?? null };
    await runner.run(
      'problem',
      payload,
      (requestKey) => reportTransferProblem(transfer.id, requestKey, payload),
      { fallback: 'The problem was not noted.' },
    );
  }, [runner, transfer.id]);

  const description = transfer.description ?? transfer.docNo ?? 'Transfer';
  return (
    <ScanCountDialog
      open
      onClose={onClose}
      verb={verb}
      eyebrow={verb === 'Pick' ? `Picking at ${from}` : `Receiving at ${to}`}
      title={`${verb} ${transfer.docNo ?? 'this transfer'}`}
      subtitle={verb === 'Pick' ? `${description} · to ${to}` : `${description} · from ${from}`}
      lines={lines}
      draftKey={draftOwner ? `${draftOwner}:transfer:${transfer.id}` : undefined}
      resolveScan={verb === 'Pick' ? resolvePickScan : undefined}
      onSubmit={verb === 'Pick' ? submitPick : submitReceive}
      onReportProblem={actions.report_problem.allowed ? reportProblem : undefined}
    />
  );
}
